import React, { useMemo } from 'react';
import { Field, LinkField } from '@sitecore-jss/sitecore-jss-nextjs';
import { v4 as uuidv4 } from 'uuid';

import { icon_arrow_left, icon_arrow_right, icon_error } from '@gn-audio/icons';
import { Button } from '@gn-audio/jss-components';
import { JabraIcon } from '@gn-audio/jss-components';

import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import Slider, { SliderProps } from 'components/Product/Slider/Slider';

export type ProductNotAvailableSpotProps = {
  fields: ProductNotAvailableSpotFields;
};

type ProductNotAvailableSpotFields = {
  MainButtonText: Field<string>;
  MainButtonLink: LinkField;
  SecondaryButtonText: Field<string>;
  SecondaryButtonLink: LinkField;
  Title: Field<string>;
  Description: Field<string>;
  Sliders: SliderProps[];
};

const ProductNotAvailableSpot = ({ fields }: ProductNotAvailableSpotProps): React.ReactElement => {
  const pageFields =
    fields &&
    mapSharedPropertiesToFields<ProductNotAvailableSpotFields>(fields as SitecoreFieldTypes);

  const {
    Title,
    Description,
    MainButtonText,
    SecondaryButtonText,
    MainButtonLink,
    SecondaryButtonLink,
    Sliders,
  } = pageFields;

  const splitDescription = useMemo(() => {
    return Description?.value?.split('.').map((sentence) => ({
      id: uuidv4(),
      text: sentence,
    }));
  }, [Description]);

  return (
    <div className="container-fluid overflow-hidden bg-beige-50 py-24">
      <div className="container">
        <div className="light flex flex-col items-center justify-center gap-8 pb-28 align-top">
          <div className="h-20 w-20 rounded-full bg-beige-100 p-4">
            <JabraIcon Icon={icon_error} />
          </div>
          <div>
            <h1 className="heading-1 text-center">{Title?.value}</h1>
            <p className="paragraph-large mb-6 text-center">
              {splitDescription?.map((item, index) => (
                <React.Fragment key={item.id}>
                  {item.text + '. '}
                  {index < splitDescription.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
          <div className="buttonRow flex flex-col gap-4 md:flex-row md:gap-2">
            {!!MainButtonText?.value && (
              <Button
                asLink
                href={MainButtonLink?.value.href}
                variant="primary"
                size="m"
                iconPlacement="left"
                icon={<JabraIcon Icon={icon_arrow_left} />}
              >
                {MainButtonText?.value}
              </Button>
            )}
            {!!SecondaryButtonText?.value && (
              <Button
                asLink
                href={SecondaryButtonLink?.value.href}
                variant="secondary"
                size="m"
                iconPlacement="right"
                icon={<JabraIcon Icon={icon_arrow_right} />}
              >
                {SecondaryButtonText?.value}
              </Button>
            )}
          </div>
        </div>
        {Sliders.map((slider) => (
          <Slider key={slider.id} Header={slider.Header} Cards={slider.Cards} />
        ))}
      </div>
    </div>
  );
};

export default ProductNotAvailableSpot;
